import { z } from 'zod';
import { PaginationMetaSchema } from './common.js';
import { BedrockCostEstimateSchema, BedrockCostSummarySchema } from './ai-usage-cost.js';
export const AI_USAGE_KINDS = {
    generative: '생성·대화 AI',
    vision: '사진 검수 AI',
};
export function aiUsageKind(provider) {
    return provider === 'rekognition' ? 'vision' : 'generative';
}
export const AI_USAGE_PROVIDERS = ['anthropic', 'bedrock', 'openai', 'gemini', 'rekognition'];
export const AI_USAGE_GROUPS = {
    image: 'AI 사진 생성',
    chatbot: '챗봇',
    contact: '문의 AI',
    template: '템플릿 변환',
    analytics: '분석·리포트',
    rekognition: '얼굴 검수',
    other: '기타',
};
export const AI_USAGE_PURPOSES = {
    'image.generate': { label: 'AI 사진 생성', group: 'image' },
    'image.edit': { label: 'AI 사진 편집', group: 'image' },
    'chatbot.turn': { label: '챗봇', group: 'chatbot' },
    'contact.draft': { label: '문의 답변 초안', group: 'contact' },
    classify_inquiry: { label: '문의 태깅', group: 'contact' },
    record_diagnosis: { label: '문의 진단', group: 'contact' },
    match_faqs: { label: 'FAQ 추천', group: 'contact' },
    propose_faq_candidate: { label: 'FAQ 후보 생성', group: 'contact' },
    'template.bind': { label: '템플릿 바인딩', group: 'template' },
    'template.convert': { label: '템플릿 변환', group: 'template' },
    interpret_analytics_question: { label: '지표 질문 해석', group: 'analytics' },
    dashboard_chat: { label: '대시보드 대화', group: 'analytics' },
    recommend_metrics: { label: '지표 추천', group: 'analytics' },
    draft_metric_definition: { label: '지표 정의 초안', group: 'analytics' },
    summarize_analytics: { label: '지표 요약', group: 'analytics' },
    'ads.insights': { label: '광고 인사이트', group: 'analytics' },
    'smartstore.price_insights': { label: '가격 인사이트', group: 'analytics' },
    'reports.revenue': { label: '매출 리포트', group: 'analytics' },
};
export function aiUsagePurpose(purpose) {
    if (purpose.startsWith('rekognition.'))
        return { label: '얼굴 검수', group: 'rekognition' };
    return Object.hasOwn(AI_USAGE_PURPOSES, purpose)
        ? AI_USAGE_PURPOSES[purpose]
        : { label: purpose, group: 'other' };
}
export const AI_USAGE_STATUSES = {
    pending: '진행 중',
    succeeded: '성공',
    failed: '실패',
    aborted: '중단',
};
export const AI_USAGE_MEASUREMENTS = {
    measured: '완전 측정',
    partial: '부분 측정',
    unavailable: '미확인',
    not_applicable: '대상 아님',
};
export const AiUsageQuerySchema = z
    .object({
    from: z.iso.date(),
    to: z.iso.date(),
    provider: z.enum(AI_USAGE_PROVIDERS).optional(),
    kind: z.enum(['generative', 'vision']).optional(),
    group: z
        .enum(['image', 'chatbot', 'contact', 'template', 'analytics', 'rekognition', 'other'])
        .optional(),
    status: z.enum(['pending', 'succeeded', 'failed', 'aborted']).optional(),
    search: z.string().trim().max(128).default(''),
    tab: z.enum(['all', 'failed', 'incomplete']).default('all'),
    page: z.coerce.number().int().min(1).max(100000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    sortBy: z.enum(['startedAt', 'inputTokens', 'outputTokens', 'durationMs']).default('startedAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
})
    .refine((q) => q.from <= q.to, {
    message: '시작일은 종료일보다 늦을 수 없습니다',
    path: ['to'],
})
    .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86400000 <= 186, {
    message: '조회 기간은 최대 187일입니다',
    path: ['to'],
});
const tokens = z.number().nonnegative().nullable();
export const AiUsageItemSchema = z.object({
    id: z.string(),
    operationId: z.string(),
    callId: z.string(),
    attempt: z.number().int().positive(),
    userId: z.string().nullable(),
    jobId: z.string().nullable(),
    route: z.string().nullable(),
    provider: z.string(),
    purpose: z.string(),
    model: z.string(),
    responseModel: z.string().nullable(),
    providerRequestId: z.string().nullable(),
    status: z.enum(['pending', 'succeeded', 'failed', 'aborted']),
    usageStatus: z.enum(['measured', 'partial', 'unavailable', 'not_applicable']),
    inputTokens: tokens,
    outputTokens: tokens,
    cacheReadTokens: tokens,
    cacheWriteTokens: tokens,
    thinkingTokens: tokens,
    startedAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullable(),
    durationMs: z.number().nullable(),
    stopReason: z.string().nullable(),
    errorCode: z.string().nullable(),
    httpStatus: z.number().nullable(),
    costEstimate: BedrockCostEstimateSchema.optional(),
});
export const AiUsageSummarySchema = z.object({
    total: z.number(),
    succeeded: z.number(),
    failed: z.number(),
    aborted: z.number(),
    pending: z.number(),
    measured: z.number(),
    eligible: z.number(),
    incomplete: z.number(),
    notApplicable: z.number(),
    inputTokens: tokens,
    outputTokens: tokens,
    bedrockCost: BedrockCostSummarySchema.optional(),
});
export const AiUsageBucketSchema = z.object({
    date: z.string(),
    purpose: z.string(),
    provider: z.string(),
    count: z.number(),
    inputTokens: tokens,
    outputTokens: tokens,
});
export const AiUsageListSchema = z.object({
    items: z.array(AiUsageItemSchema),
    pagination: PaginationMetaSchema,
    summary: AiUsageSummarySchema,
    buckets: z.array(AiUsageBucketSchema),
    queriedAt: z.iso.datetime(),
});
export const AiUsageDetailSchema = z.object({
    item: AiUsageItemSchema,
    attempts: z.array(AiUsageItemSchema),
});
//# sourceMappingURL=ai-usage.js.map